// F-091 Équipes et attribution du travail.
import { useState } from "react";
import { del, patch, post, put } from "@core/api";
import { date } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { ROLE_LABELS, type Role } from "@core/session";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Input, Loading, Notice, PageHeader, Select, Table, Tabs, Textarea, fieldError } from "@core/ui";
import { PRIORITIES, Status, useMemberOptions, usePropertyOptions, useQuick, useRun } from "./shared";

type Tab = "charge" | "taches" | "portefeuilles" | "equipes" | "reglages";

export function TeamPage() {
  const org = useOrg();
  const [tab, setTab] = useState<Tab>("charge");
  return (
    <div className="page">
      <PageHeader eyebrow="Professionnels" title="Équipe et attribution du travail" intro="Qui s’occupe de quels logements, quelles tâches restent ouvertes et comment répartir la charge. Chaque changement d’attribution est journalisé." />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        items={[
          { value: "charge", label: "Charge de travail" },
          { value: "taches", label: "Tâches" },
          { value: "portefeuilles", label: "Portefeuilles" },
          { value: "equipes", label: "Équipes" },
          ...(org.can("members") ? [{ value: "reglages" as Tab, label: "Périmètre" }] : []),
        ]}
      />
      {tab === "charge" ? <Workload /> : null}
      {tab === "taches" ? <Tasks /> : null}
      {tab === "portefeuilles" ? <Assignments /> : null}
      {tab === "equipes" ? <Teams /> : null}
      {tab === "reglages" ? <ScopeSettings /> : null}
    </div>
  );
}

function Workload() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/workload"));
  if (q.isLoading) return <Loading />;
  return (
    <Card title="Charge de travail par membre">
      <ErrorBox error={q.error} />
      {q.data && (q.data.unassigned_properties > 0 || q.data.unassigned_tasks > 0) ? (
        <Notice tone="attention">
          {q.data.unassigned_properties} logement(s) sans responsable et {q.data.unassigned_tasks} tâche(s) ouverte(s) sans personne attribuée.
        </Notice>
      ) : null}
      <Table caption="Charge de travail">
        <thead>
          <tr>
            <th>Membre</th>
            <th>Rôle</th>
            <th>Équipes</th>
            <th className="num">Logements</th>
            <th className="num">Tâches ouvertes</th>
            <th className="num">En retard</th>
            <th className="num">Priorité haute</th>
          </tr>
        </thead>
        <tbody>
          {(q.data?.items ?? []).map((m: any) => (
            <tr key={m.user_id}>
              <td>{m.full_name}</td>
              <td>{ROLE_LABELS[m.role as Role] ?? m.role}</td>
              <td>{(m.teams as string[]).join(", ") || "—"}</td>
              <td className="num">{m.properties}</td>
              <td className="num">{m.open_tasks}</td>
              <td className="num">{m.overdue_tasks > 0 ? <Badge tone="attention">{m.overdue_tasks} en retard</Badge> : 0}</td>
              <td className="num">{m.high_priority}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {org.can("members") ? <Reassign /> : null}
    </Card>
  );
}

function Reassign() {
  const org = useOrg();
  const members = useMemberOptions(org.orgId);
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ from_user_id: "", to_user_id: "", include_open_tasks: true });
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        const r: any = await run(() => post(org.api("/pro/reassign"), form));
        if (r) {
          invalidate(org.api("/pro"));
          setForm({ ...form, from_user_id: "", to_user_id: "" });
        }
      }}
    >
      <h3>Départ, congé ou remplacement</h3>
      <p className="muted small">Transfère le portefeuille (et, si vous le souhaitez, les tâches ouvertes) d’une personne à une autre. Retirer l’accès à l’espace se fait dans « Membres et droits ».</p>
      <div className="form-row">
        <Select label="Personne qui quitte le portefeuille" value={form.from_user_id} options={members} onChange={(e) => setForm({ ...form, from_user_id: e.target.value })} />
        <Select label="Personne qui reprend" value={form.to_user_id} options={members} error={fieldError(error, "to_user_id")} onChange={(e) => setForm({ ...form, to_user_id: e.target.value })} />
      </div>
      <Checkbox label="Transférer aussi les tâches ouvertes" checked={form.include_open_tasks} onChange={(v) => setForm({ ...form, include_open_tasks: v })} />
      <ErrorBox error={error && !fieldError(error, "to_user_id") ? error : null} />
      <div>
        <Button type="submit" variant="secondaire" busy={busy} disabled={!form.from_user_id || !form.to_user_id}>
          Réattribuer
        </Button>
      </div>
    </form>
  );
}

function Tasks() {
  const org = useOrg();
  const members = useMemberOptions(org.orgId, "Toutes les personnes");
  const assignees = useMemberOptions(org.orgId, "Personne");
  const { options: properties } = usePropertyOptions(org.orgId, "Aucun logement");
  const [filter, setFilter] = useState({ assignee_user_id: "", status: "" });
  const params = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]).toString();
  const q = useGet<any>(org.api(`/pro/tasks${params ? `?${params}` : ""}`));
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ title: "", notes: "", due_on: "", priority: "normale", assignee_user_id: "", property_id: "" });
  const change = async (id: string, body: any, msg: string) => {
    if (await quick(() => patch(org.api(`/pro/tasks/${id}`), body), msg)) invalidate(org.api("/pro"));
  };
  return (
    <div className="stack">
      <Card title="Tâches">
        <div className="form-row">
          <Select label="Attribuée à" value={filter.assignee_user_id} options={members} onChange={(e) => setFilter({ ...filter, assignee_user_id: e.target.value })} />
          <Select
            label="Statut"
            value={filter.status}
            options={[{ value: "", label: "Tous" }, { value: "a_faire", label: "À faire" }, { value: "en_cours", label: "En cours" }, { value: "fait", label: "Fait" }, { value: "annule", label: "Annulé" }]}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          />
        </div>
        {q.isLoading ? <Loading /> : null}
        {q.data && !q.data.items.length ? <Empty title="Aucune tâche">Les rappels d’échéances de baux et les tâches créées ici apparaissent dans cette liste.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Tâches">
            <thead>
              <tr>
                <th>Tâche</th>
                <th>Logement</th>
                <th>Échéance</th>
                <th>Priorité</th>
                <th>Attribuée à</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((t: any) => (
                <tr key={t.id}>
                  <td>
                    {t.title}
                    {t.notes ? <span className="block muted small">{t.notes}</span> : null}
                  </td>
                  <td>{t.related_label ?? "—"}</td>
                  <td>{date(t.due_on)}</td>
                  <td>{PRIORITIES[t.priority] ?? t.priority}</td>
                  <td>
                    {org.can("operate") ? (
                      <select
                        aria-label={`Personne attribuée à « ${t.title} »`}
                        value={t.assignee_user_id ?? ""}
                        onChange={(e) => change(t.id, e.target.value ? { assignee_user_id: e.target.value } : { unassign: true }, "Attribution modifiée.")}
                      >
                        {assignees.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      t.assignee_name ?? "—"
                    )}
                  </td>
                  <td>
                    <Status kind="task" value={t.status} />
                    {org.can("operate") && t.status !== "fait" ? (
                      <Button variant="discret" small onClick={() => change(t.id, { status: "fait" }, "Tâche terminée.")}>
                        Marquer comme faite
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {org.can("operate") ? (
        <Card title="Nouvelle tâche">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const body = { ...form, due_on: form.due_on || null, assignee_user_id: form.assignee_user_id || null, property_id: form.property_id || null };
              if (await run(() => post(org.api("/pro/tasks"), body), "Tâche créée.")) {
                setForm({ ...form, title: "", notes: "" });
                invalidate(org.api("/pro"));
              }
            }}
          >
            <Input label="Intitulé" required value={form.title} error={fieldError(error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <div className="form-row">
              <Select label="Logement" value={form.property_id} options={properties} onChange={(e) => setForm({ ...form, property_id: e.target.value })} />
              <Select label="Attribuée à" value={form.assignee_user_id} options={assignees} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} />
              <Input label="Échéance" type="date" value={form.due_on} onChange={(e) => setForm({ ...form, due_on: e.target.value })} />
              <Select label="Priorité" value={form.priority} options={Object.entries(PRIORITIES).map(([value, label]) => ({ value, label }))} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
            </div>
            <Textarea label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <ErrorBox error={error && !fieldError(error, "title") ? error : null} />
            <div>
              <Button type="submit" variant="secondaire" busy={busy}>
                Créer la tâche
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

function Assignments() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/assignments"));
  const teams = useGet<any>(org.api("/pro/teams"));
  const members = useMemberOptions(org.orgId);
  const { options: properties } = usePropertyOptions(org.orgId);
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ property_id: "", user_id: "", team_id: "", role: "responsable" });
  return (
    <div className="stack">
      <Card title="Portefeuilles attribués">
        {q.isLoading ? <Loading /> : null}
        {q.data && !q.data.items.length ? <Empty title="Aucun logement attribué">Attribuez chaque logement à un responsable et, si besoin, à un suppléant.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Attributions">
            <thead>
              <tr>
                <th>Logement</th>
                <th>Personne</th>
                <th>Rôle</th>
                <th>Équipe</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((a: any) => (
                <tr key={a.id}>
                  <td>{a.property_label}</td>
                  <td>{a.user_name}</td>
                  <td>{a.role === "responsable" ? "Responsable" : "Suppléant"}</td>
                  <td>{a.team_name ?? "—"}</td>
                  <td className="right">
                    {org.can("members") ? (
                      <Button variant="discret" small onClick={async () => (await quick(() => del(org.api(`/pro/assignments/${a.id}`)), "Attribution retirée.")) && invalidate(org.api("/pro"))}>
                        Retirer
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {org.can("members") ? (
        <Card title="Attribuer un logement">
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await run(() => post(org.api("/pro/assignments"), { ...form, team_id: form.team_id || null }), "Logement attribué.")) invalidate(org.api("/pro"));
            }}
          >
            <div className="form-row">
              <Select label="Logement" value={form.property_id} options={properties} onChange={(e) => setForm({ ...form, property_id: e.target.value })} />
              <Select label="Personne" value={form.user_id} options={members} error={fieldError(error, "user_id")} onChange={(e) => setForm({ ...form, user_id: e.target.value })} />
              <Select
                label="Rôle"
                value={form.role}
                options={[
                  { value: "responsable", label: "Responsable" },
                  { value: "suppleant", label: "Suppléant" },
                ]}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              />
              <Select
                label="Équipe"
                value={form.team_id}
                options={[{ value: "", label: "Aucune" }, ...(teams.data?.items ?? []).map((t: any) => ({ value: t.id, label: t.name }))]}
                onChange={(e) => setForm({ ...form, team_id: e.target.value })}
              />
            </div>
            <ErrorBox error={error && !fieldError(error, "user_id") ? error : null} />
            <div>
              <Button type="submit" variant="secondaire" busy={busy} disabled={!form.property_id || !form.user_id}>
                Attribuer
              </Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

function Teams() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/teams"));
  const members = useMemberOptions(org.orgId, "Ajouter un membre");
  const invalidate = useInvalidate();
  const quick = useQuick();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({ name: "", description: "" });
  return (
    <div className="stack">
      {q.isLoading ? <Loading /> : null}
      {q.data && !q.data.items.length ? (
        <Card>
          <Empty title="Aucune équipe">Regroupez les gestionnaires par secteur ou par type de biens pour répartir les portefeuilles.</Empty>
        </Card>
      ) : null}
      {(q.data?.items ?? []).map((t: any) => (
        <Card
          key={t.id}
          title={t.name}
          actions={
            org.can("members") ? (
              <Button variant="discret" small onClick={async () => (await quick(() => del(org.api(`/pro/teams/${t.id}`)), "Équipe archivée.")) && invalidate(org.api("/pro"))}>
                Archiver
              </Button>
            ) : null
          }
        >
          {t.description ? <p className="muted">{t.description}</p> : null}
          <p className="small muted">{t.properties} logement(s) attribué(s) via cette équipe.</p>
          <ul className="list-plain">
            {(t.members as any[]).map((m) => (
              <li key={m.user_id} className="row-between">
                <span>
                  {m.full_name} {m.team_role === "responsable" ? <Badge tone="encre">Responsable</Badge> : null}
                </span>
                {org.can("members") ? (
                  <Button variant="discret" small onClick={async () => (await quick(() => del(org.api(`/pro/teams/${t.id}/members/${m.user_id}`)), "Membre retiré de l’équipe.")) && invalidate(org.api("/pro"))}>
                    Retirer
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {org.can("members") ? (
            <label className="small">
              <span className="sr-only">Ajouter un membre à {t.name}</span>
              <select
                value=""
                onChange={async (e) => {
                  if (!e.target.value) return;
                  if (await quick(() => post(org.api(`/pro/teams/${t.id}/members`), { user_id: e.target.value }), "Membre ajouté.")) invalidate(org.api("/pro"));
                }}
              >
                {members.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </Card>
      ))}
      {org.can("members") ? (
        <Card title="Créer une équipe">
          <form
            className="form-row"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await run(() => post(org.api("/pro/teams"), form), "Équipe créée.")) {
                setForm({ name: "", description: "" });
                invalidate(org.api("/pro"));
              }
            }}
          >
            <Input label="Nom" required value={form.name} error={fieldError(error, "name")} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Button type="submit" variant="secondaire" busy={busy}>
              Créer
            </Button>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

function ScopeSettings() {
  const org = useOrg();
  const q = useGet<any>(org.api("/pro/settings"));
  const invalidate = useInvalidate();
  const { busy, error, run } = useRun();
  if (!q.data) return <Loading />;
  return (
    <Card title="Périmètre des collaborateurs">
      <p>Lorsque le périmètre est restreint, une personne au rôle « Collaborateur » ne voit dans les outils professionnels que les logements qui lui sont attribués (tâches, baux commerciaux, dépenses, actions groupées).</p>
      <Notice tone="attention">
        Les autres rubriques de l’application (logements, candidatures, loyers) appliquent leurs propres règles d’accès : ce réglage ne les restreint pas encore.
      </Notice>
      <Checkbox
        label="Restreindre les collaborateurs à leur portefeuille"
        checked={q.data.restrict_collaborators}
        onChange={async (v) => {
          if (await run(() => put(org.api("/pro/settings"), { restrict_collaborators: v }), v ? "Périmètre restreint activé." : "Périmètre restreint désactivé.")) invalidate(org.api("/pro"));
        }}
      />
      {busy ? <Loading label="Enregistrement…" /> : null}
      <ErrorBox error={error} />
    </Card>
  );
}
